import { Box, Grid, Popup } from '@approvalmax/ui/src/components';
import { miscHelpers } from '@approvalmax/utils';
import { clearCacheStorageAmaxPayPaymentDetails } from 'modules/data-providers';
import { useDispatch } from 'modules/react-redux';
import { memo, useCallback, useState } from 'react';
import { useToggle } from 'react-use';
import {
    AuditTrail,
    EditCounterpartyPaymentDetailsPopupContent,
    EditPaymentDetailsPopupContent,
} from 'shared/components';
import { useGetXeroCounterpartyPaymentDetails } from 'shared/data/webApp/v1';
import { useGetXeroCounterpartyPaymentDetailsHistory } from 'shared/data/webApp/v2';
import { useCompanyBetaFeatures, useCompanyIdBasedOnUrl } from 'shared/hooks';

import { EmptyPaymentDetails, FinancialDetails, Header, PaymentDetails } from './components';
import { useFinancialDetailsData, usePaymentDetailsHistoryAuditTrail } from './InfoPanel.hooks';
import { InfoPanelProps } from './InfoPanel.types';

export const InfoPanel = memo<InfoPanelProps>((props) => {
    const { activeContact, updateContactListCacheData } = props;

    const paymentDetailsData = activeContact?.paymentDetails ?? [];

    const [isEditPopupOpened, toggleEditPopupOpened] = useToggle(false);

    const dispatch = useDispatch();

    const { companyId } = useCompanyIdBasedOnUrl();
    const { isConfirmationOfPayee, isWalletsUK } = useCompanyBetaFeatures(companyId);

    const [selectedPaymentDetailsId, setSelectedPaymentDetailsId] = useState<string | null>(null);

    const { refetch: refetchXeroCounterpartyPaymentDetails } = useGetXeroCounterpartyPaymentDetails(
        { path: { companyId, counterpartyId: activeContact?.counterpartyId } },
        {
            enabled: false,
        }
    );

    const {
        data: paymentDetailsHistory,
        refetch: refetchPaymentDetailsHistory,
        isFetching: isFetchingPaymentDetailsHistory,
    } = useGetXeroCounterpartyPaymentDetailsHistory({
        path: {
            companyId,
            counterpartyId: activeContact?.counterpartyId,
        },
    });

    const openEditPopup = useCallback(
        (paymentDetailsId?: string | null) => {
            setSelectedPaymentDetailsId(typeof paymentDetailsId === 'string' ? paymentDetailsId : null);

            toggleEditPopupOpened(true);
        },
        [toggleEditPopupOpened]
    );

    const onPaymentDetailsChanged = useCallback(async () => {
        toggleEditPopupOpened(false);

        dispatch(clearCacheStorageAmaxPayPaymentDetails());

        const newPaymentDetails = await refetchXeroCounterpartyPaymentDetails();

        updateContactListCacheData((currentData) => {
            return {
                ...currentData,
                pages: currentData.pages.map((page) => {
                    return {
                        ...page,
                        items: page.items.map((item) => {
                            if (item.counterpartyId === activeContact?.counterpartyId) {
                                return {
                                    ...item,
                                    paymentDetails: newPaymentDetails.data?.items ?? [],
                                };
                            }

                            return item;
                        }),
                    };
                }),
            };
        });

        void refetchPaymentDetailsHistory();
    }, [
        toggleEditPopupOpened,
        dispatch,
        activeContact,
        updateContactListCacheData,
        refetchXeroCounterpartyPaymentDetails,
        refetchPaymentDetailsHistory,
    ]);

    const financialDetails = useFinancialDetailsData(activeContact);

    const auditTrailData = usePaymentDetailsHistoryAuditTrail(paymentDetailsHistory);

    if (!activeContact) {
        return null;
    }

    const noPaymentDetails = activeContact.paymentDetails.length === 0;

    return (
        <>
            <Box spacing='16 60' height='100%' overflowY='auto'>
                <Grid rowGap={24}>
                    <Header
                        title={activeContact.name}
                        externalUrl={activeContact.url}
                        onSync={miscHelpers.noop}
                        onAddPaymentDetails={openEditPopup}
                    />

                    {noPaymentDetails && <EmptyPaymentDetails onAdd={openEditPopup} />}

                    {paymentDetailsData.map((item) => (
                        <PaymentDetails key={item.id} data={item} onEdit={openEditPopup} />
                    ))}

                    <FinancialDetails data={financialDetails} />

                    <AuditTrail
                        id={`payment-details-${activeContact.counterpartyId}`}
                        isLoading={isFetchingPaymentDetailsHistory}
                        items={auditTrailData}
                    />
                </Grid>
            </Box>

            <Popup open={isEditPopupOpened} onToggle={toggleEditPopupOpened} preventAutoClose size='medium'>
                {isWalletsUK || isConfirmationOfPayee ? (
                    <EditCounterpartyPaymentDetailsPopupContent
                        paymentDetailsId={selectedPaymentDetailsId}
                        contact={activeContact}
                    />
                ) : (
                    <EditPaymentDetailsPopupContent
                        paymentDetailsId={selectedPaymentDetailsId}
                        paymentDetails={paymentDetailsData}
                        onPaymentDetailsChanged={onPaymentDetailsChanged}
                        contact={activeContact}
                    />
                )}
            </Popup>
        </>
    );
});

InfoPanel.displayName = 'InfoPanel';
